import { useState } from "react";
import { useResource } from "../lib/hooks";
import { api } from "../lib/api";

interface Count { done: number; total: number }
interface AdminOnboardingView {
    account: {
        userId: string | null; email: string | null; role: string | null;
        hasLoggedIn: boolean; onboardingComplete: boolean; pendingInvite: boolean; linked: boolean;
    };
    sections: { employeeInfo: Count; profile: Count; documents: Count; policies: Count } | null;
    adminPct: number;
    userPct: number;
    state: "in_progress" | "ready" | "approved";
    approvedAt: string | null;
    approvedBy: string | null;
    policies: Array<{ policyGroupId: string; name: string; version: string; required: boolean; acknowledged: boolean; acknowledgedAt: string | null }>;
    documents: Array<{ id: string; type_name: string; expiry_date: string | null; verification_status: string; expiry_status: string }>;
}

const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString("en-AU") : "");
const STATE_LABEL: Record<AdminOnboardingView["state"], string> = {
    in_progress: "In progress", ready: "Ready for review", approved: "Approved",
};

export function OnboardingDetail({ employeeId, onChanged }: { employeeId: string; onChanged?: () => void }) {
    const ob = useResource<AdminOnboardingView>(`/employees/${employeeId}/onboarding`);
    const [hideCompleted, setHideCompleted] = useState(false);
    const [busy, setBusy] = useState(false);

    if (ob.loading || !ob.data) return <div className="card"><div className="card-body muted">Loading…</div></div>;
    const d = ob.data;
    const reload = () => { ob.reload(); onChanged?.(); };

    const act = async (fn: () => Promise<unknown>) => { setBusy(true); try { await fn(); reload(); } finally { setBusy(false); } };
    const approve = () => act(() => api.post(`/employees/${employeeId}/onboarding/approve`, {}));
    const inviteAgain = () => act(() => api.post("/invites", { email: d.account.email, role: d.account.role ?? "staff", employeeId }));
    const verify = (id: string) => act(() => api.post(`/employee-documents/${id}/verify`, {}));

    const policies = hideCompleted ? d.policies.filter((p) => !p.acknowledged) : d.policies;
    const documents = hideCompleted ? d.documents.filter((x) => x.verification_status !== "verified") : d.documents;
    const bar = (label: string, pctVal: number) => (
        <div>
            <div className="onboard-meter"><span>{label}</span><span style={{ marginLeft: "auto" }}>{pctVal}%</span></div>
            <div className="onboard-meter-bar"><i style={{ width: `${pctVal}%` }} /></div>
        </div>
    );

    return (
        <div className="card">
            <div className="card-head" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <h2>Onboarding</h2>
                <span className={`chip ${d.state === "approved" ? "pass" : d.state === "ready" ? "info" : "warn"}`}>{STATE_LABEL[d.state]}</span>
                {!d.account.hasLoggedIn && (
                    <span className="chip mute" title="Has not logged in yet">
                        Not logged in
                        {d.account.email && <button className="btn ghost small" style={{ marginLeft: 6 }} disabled={busy} onClick={inviteAgain}>Invite again</button>}
                    </span>
                )}
                <label className="check" style={{ marginLeft: "auto" }}>
                    <input type="checkbox" checked={hideCompleted} onChange={(e) => setHideCompleted(e.target.checked)} />
                    <span>Hide completed items</span>
                </label>
            </div>
            <div className="card-body">
                <div className="onboard-status-grid" style={{ marginBottom: "0.8rem" }}>
                    {bar("Admin", d.adminPct)}
                    {bar("User", d.userPct)}
                </div>

                {d.sections && (
                    <div className="onboard-status-grid" style={{ marginBottom: "0.8rem", fontSize: "0.82rem" }}>
                        <span>Employee info <b>{d.sections.employeeInfo.done}/{d.sections.employeeInfo.total}</b></span>
                        <span>Profile <b>{d.sections.profile.done}/{d.sections.profile.total}</b></span>
                        <span>Documents <b>{d.sections.documents.done}/{d.sections.documents.total}</b></span>
                        <span>Policies <b>{d.sections.policies.done}/{d.sections.policies.total}</b></span>
                    </div>
                )}

                {d.state === "ready" && (
                    <button className="btn" disabled={busy} onClick={approve} style={{ marginBottom: "0.8rem" }}>Approve onboarding</button>
                )}
                {d.state === "approved" && (
                    <p className="muted" style={{ marginBottom: "0.8rem" }}>Approved {fmt(d.approvedAt)}{d.approvedBy ? ` by ${d.approvedBy}` : ""}.</p>
                )}

                {policies.length > 0 && (
                    <table style={{ width: "100%", marginBottom: "0.8rem" }}>
                        <thead><tr><th style={{ textAlign: "left" }}>Policy</th><th>Version</th><th>Status</th></tr></thead>
                        <tbody>
                            {policies.map((p) => (
                                <tr key={p.policyGroupId}>
                                    <td>{p.name}{p.required ? "" : " (optional)"}</td>
                                    <td style={{ textAlign: "center" }}>v{p.version}</td>
                                    <td style={{ textAlign: "center" }}>{p.acknowledged
                                        ? <span className="chip pass">Acknowledged {fmt(p.acknowledgedAt)}</span>
                                        : <span className="chip warn">Pending</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {documents.length > 0 && (
                    <table style={{ width: "100%" }}>
                        <thead><tr><th style={{ textAlign: "left" }}>Document</th><th>Expiry</th><th>Status</th><th></th></tr></thead>
                        <tbody>
                            {documents.map((x) => (
                                <tr key={x.id}>
                                    <td>{x.type_name}</td>
                                    <td style={{ textAlign: "center" }}>{x.expiry_date ? fmt(x.expiry_date) : "—"}</td>
                                    <td style={{ textAlign: "center" }}>{x.verification_status === "verified"
                                        ? <span className="chip pass">Verified</span>
                                        : <span className="chip warn">Pending review</span>}</td>
                                    <td style={{ textAlign: "right" }}>{x.verification_status !== "verified" &&
                                        <button className="btn ghost small" disabled={busy} onClick={() => verify(x.id)}>Verify</button>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
